import { ApiError, networkError } from './api-error';
/** Protocol shared with the assistant route: start / delta / done / error, one JSON object per line. */
export const readMessageStream = async <T>(
  response: Response,
  onDelta: (text: string) => void,
): Promise<T> => {
  if (!response.body)
    throw new ApiError({
      status: 502,
      code: 'INTERNAL_ERROR',
      message: 'Le flux de réponse est indisponible.',
    });
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completed = false;
  let result: T | undefined;
  const consume = (line: string) => {
    if (!line.trim()) return;
    const frame = JSON.parse(line) as {
      type?: string;
      text?: unknown;
      data?: T;
      error?: { message?: string; status?: number; code?: string };
    };
    if (frame.type === 'delta' && typeof frame.text === 'string') onDelta(frame.text);
    else if (frame.type === 'done') {
      completed = true;
      result = frame.data;
    } else if (frame.type === 'error')
      throw new ApiError({
        status: frame.error?.status ?? 500,
        code: frame.error?.code ?? 'INTERNAL_ERROR',
        message: frame.error?.message ?? 'La réponse a été interrompue.',
      });
  };
  try {
    while (!completed) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      if (buffer.length > 2_000_000) throw new Error('Invalid stream');
      let index: number;
      while ((index = buffer.indexOf('\n')) >= 0) {
        consume(buffer.slice(0, index));
        buffer = buffer.slice(index + 1);
      }
      if (done) {
        if (buffer.trim()) consume(buffer);
        break;
      }
    }
    if (!completed || result === undefined)
      throw new ApiError({
        status: 502,
        code: 'INTERNAL_ERROR',
        message:
          'La connexion a été interrompue. La réponse n’est pas confirmée ; vous pouvez réessayer.',
      });
    return result;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw networkError(error);
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
};
